import { FormEvent, useCallback, useEffect, useState } from "react";
import { ArrowRight, LoaderCircle, Mail, MapPin, Phone, X } from "lucide-react";

import { addLeadNote, getLead, updateLeadStatus } from "./api";
import type { LeadDetail } from "./types";

const statuses = [
  "NEW",
  "CONTACTED",
  "QUALIFYING",
  "QUOTE_PENDING",
  "AVAILABILITY_REVIEW",
  "WAITING_CUSTOMER",
  "APPROVED",
  "SCHEDULED",
  "COMPLETED",
  "LOST",
  "CANCELLED",
];

export function LeadDetailPanel({
  leadId,
  onClose,
  onChanged,
}: {
  leadId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [lead, setLead] = useState<LeadDetail>();
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      setLead(await getLead(leadId));
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [leadId]);

  useEffect(() => void load(), [load]);
  useEffect(() => {
    const close = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);

  async function changeStatus(status: string) {
    if (!lead || status === lead.status) return;
    setSaving(true);
    try {
      await updateLeadStatus(lead.id, status);
      await load();
      onChanged();
    } finally {
      setSaving(false);
    }
  }

  async function addNote(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!lead) return;
    const form = event.currentTarget;
    const body = String(new FormData(form).get("note") ?? "").trim();
    if (!body) return;
    setSaving(true);
    try {
      await addLeadNote(lead.id, body);
      form.reset();
      await load();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="drawer-layer" role="dialog" aria-modal="true" aria-label="Lead detail">
      <button className="drawer-scrim" aria-label="Close lead detail" onClick={onClose} />
      <aside className="lead-drawer">
        <header className="drawer-header">
          <div>
            <span className="eyebrow">Customer inquiry</span>
            <h2>{lead?.name ?? "Loading lead…"}</h2>
          </div>
          <button className="icon-button" aria-label="Close lead detail" onClick={onClose}>
            <X />
          </button>
        </header>

        {failed ? (
          <div className="drawer-state">
            We couldn’t load this lead.<button onClick={load}>Try again</button>
          </div>
        ) : !lead ? (
          <div className="drawer-state">
            <LoaderCircle className="spin" /> Loading details…
          </div>
        ) : (
          <div className="drawer-content">
            <section className="contact-list">
              <a href={`tel:${lead.phone}`}>
                <Phone />
                {lead.phone}
              </a>
              <a href={`mailto:${lead.email}`}>
                <Mail />
                {lead.email}
              </a>
              {stringValue(lead.intakeData.address) ? (
                <span>
                  <MapPin />
                  {stringValue(lead.intakeData.address)}
                </span>
              ) : null}
            </section>

            <section className="drawer-section">
              <span className="eyebrow">Pipeline status</span>
              <select
                value={lead.status}
                disabled={saving}
                onChange={(event) => void changeStatus(event.target.value)}
              >
                {statuses.map((status) => (
                  <option key={status} value={status}>
                    {label(status)}
                  </option>
                ))}
              </select>
            </section>

            <section className="detail-grid">
              <Detail label="Service" value={lead.serviceInterest} />
              <Detail label="Preferred date" value={lead.preferredDate} />
              <Detail label="Frequency" value={stringValue(lead.intakeData.frequency)} />
              <Detail label="Property" value={stringValue(lead.intakeData.propertyType)} />
              <Detail
                label="Approx. square footage"
                value={squareFeetValue(lead.intakeData.squareFeet)}
              />
            </section>

            {lead.message ? (
              <section className="customer-message">
                <span className="eyebrow">Customer notes</span>
                <p>{lead.message}</p>
              </section>
            ) : null}

            <form className="note-form" onSubmit={addNote}>
              <label htmlFor="lead-note">Add an internal note</label>
              <textarea
                id="lead-note"
                name="note"
                rows={3}
                maxLength={4000}
                required
                placeholder="Record the next step or customer conversation…"
              />
              <button className="primary-button" disabled={saving}>
                Save note <ArrowRight />
              </button>
            </form>

            <section className="timeline">
              <span className="eyebrow">Activity</span>
              {lead.events.length ? (
                lead.events.map((event) => (
                  <article key={event.id}>
                    <i />
                    <div>
                      <strong>{eventTitle(event.type)}</strong>
                      {event.body ? <p>{event.body}</p> : null}
                      <span>
                        {event.actorName ?? "Website"} · {formatDate(event.createdAt)}
                      </span>
                    </div>
                  </article>
                ))
              ) : (
                <p className="empty-copy">No activity recorded yet.</p>
              )}
            </section>
          </div>
        )}
      </aside>
    </div>
  );
}

function Detail({ label: title, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <span>{title}</span>
      <strong>{value || "Not specified"}</strong>
    </div>
  );
}
function stringValue(value: unknown) {
  return typeof value === "string" && value ? value : null;
}
function squareFeetValue(value: unknown) {
  return typeof value === "number" && Number.isFinite(value)
    ? `${value.toLocaleString("en-US")} sq ft`
    : null;
}
function label(value: string) {
  return value
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
}
function eventTitle(type: string) {
  return type === "NOTE_ADDED"
    ? "Internal note"
    : type === "STATUS_CHANGED"
      ? "Status updated"
      : "Inquiry received";
}
function formatDate(value: string) {
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(value),
  );
}
